import type { OrgUser, TenantContext } from '../types';
import { InviteStatus } from '../types';
import { apiClient } from './client';
import type { User } from '@jobmate/types'; // #67 shared-types


function toIsoDate(value: number | string | null | undefined): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && value.trim() !== '') {
    return new Date(numericValue).toISOString();
  }

  return value;
}

// #67: BackendUser was redundant — User from @jobmate/types covers the wire shape.
// OrgUser is the frontend domain type; fields differ (name vs first_name/last_name,
// inviteStatus/joinedAt missing from wire). Mapping is handled here.
function normaliseUser(user: User): OrgUser {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();

  return {
    id: user.id, // number — matches wire User.id
    email: user.email,
    name,
    role: user.role as TenantContext['role'],
    inviteStatus: InviteStatus.ACCEPTED,
    joinedAt: toIsoDate(user.created_at),
  };
}

export const users = {
  // #67: Backend returns User[] (raw array). Previous code used { users: [] } envelope — that was the bug from #60.
  // OrgUser is the frontend domain type; User (wire) is the wire format.
  getUsers: async (orgSlug: string): Promise<OrgUser[]> => {
    const { data } = await apiClient.get<User[]>(`/api/orgs/${orgSlug}/users`);
    return data.map(normaliseUser);
  },

  inviteUser: async (orgSlug: string, email: string, role: TenantContext['role']): Promise<{ inviteId: string }> => {
    const { data } = await apiClient.post<{ inviteId: string }>(`/api/orgs/${orgSlug}/invites`, { email, role });
    return data;
  },

  updateUserRole: async (orgSlug: string, userId: number, role: TenantContext['role']): Promise<void> => {
    await apiClient.patch(`/api/orgs/${orgSlug}/users/${userId}/role`, { role });
  },

  removeUser: async (orgSlug: string, userId: number): Promise<void> => {
    await apiClient.delete(`/api/orgs/${orgSlug}/users/${userId}`);
  },
};
